<script setup>
import { ref, watch } from 'vue'
import CmsText from '@/@core/components/CmsText.vue'

/**
 * "Questions?" — the help panel behind the ? in an RM AI reply's action row.
 *
 * It replaced a speech-bubble "Message support" icon, which read as "comment"
 * rather than "get help" and so wasn't doing its job (Kapil, 9/2). Nothing was
 * removed: support is one of the routes offered here, alongside email and the
 * FAQs.
 *
 * The FAQs expand INSIDE this panel rather than opening the members modal —
 * a modal on top of a modal is a worse place to read from, and the content is
 * the same CMS key either way, so it cannot drift from the /members page.
 */

const props = defineProps({
  modelValue: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'messageSupport'])

const faqsOpen = ref(false)

// Fresh panel each time: an expansion left open from a previous visit is a
// surprise, and the first thing a member should see is the offer of help.
watch(() => props.modelValue, open => {
  if (open) faqsOpen.value = false
})

function close() {
  emit('update:modelValue', false)
}

function handleMessageSupport() {
  close()
  emit('messageSupport')
}

// Pre-filling the subject saves the member writing one and tells the RM team
// where the question came from.
const mailto = 'mailto:preferred@reserverm.com?subject=Questions%20from%20exchange%20with%20RM%20AI'
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="480"
    scrollable
    @update:model-value="emit('update:modelValue', $event)"
  >
    <VCard class="agent-help">
      <div class="agent-help__head">
        <p class="agent-help__title">
          Have questions or want guidance prior to submitting a request?
        </p>
        <VBtn
          icon="mdi-close"
          size="small"
          variant="text"
          aria-label="Close"
          @click="close"
        />
      </div>

      <VCardText class="agent-help__body">
        <p class="agent-help__lead">
          We'd love to help - especially before you submit a request.
        </p>

        <p class="agent-help__lead">
          Once you submit a request via RM, it's sent to the restaurant (RM reviews some requests first) &amp; is harder to change/update.
        </p>

        <p class="agent-help__actions">
          <a
            :href="mailto"
            class="agent-help__link"
          >Email us</a>
          <span class="agent-help__or">or</span>
          <button
            type="button"
            class="agent-help__link agent-help__link--button"
            @click="handleMessageSupport"
          >
            message support
          </button>
        </p>

        <p class="agent-help__timing">
          We'll do our best to respond within a few hours — especially between 7am–10pm ET
        </p>

        <!--
          FAQs in place. Same CMS key the /members page uses, so the two can
          never say different things.
        -->
        <button
          type="button"
          class="agent-help__faq-toggle"
          :aria-expanded="faqsOpen ? 'true' : 'false'"
          @click="faqsOpen = !faqsOpen"
        >
          Or if helpful, you can review answers to FAQs here
          <VIcon
            :icon="faqsOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'"
            size="18"
          />
        </button>

        <VExpandTransition>
          <div
            v-if="faqsOpen"
            class="agent-help__faqs"
          >
            <CmsText
              k="members.tabs.faqs"
              tag="div"
              class="agent-help__faqs-body"
            />
          </div>
        </VExpandTransition>
      </VCardText>
    </VCard>
  </VDialog>
</template>

<style scoped>
.agent-help {
  border-radius: 16px;
}

.agent-help__head {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 20px 16px 0 20px;
}

.agent-help__title {
  margin: 0;
  flex: 1;
  font-family: var(--ds-font-heading);
  font-size: 19px;
  font-weight: var(--ds-font-medium);
  line-height: 1.3;
  color: var(--ds-brand-ink);
}

.agent-help__body {
  padding: 12px 20px 20px;
}

.agent-help__lead {
  margin: 0 0 10px;
  font-size: 15px;
  line-height: 1.5;
  color: var(--ds-text-body);
}

/* The last lead paragraph carries the gap down to the actions. */
.agent-help__lead:last-of-type {
  margin-bottom: 18px;
}

.agent-help__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
  margin: 0 0 6px;
  font-size: 15px;
}

.agent-help__or {
  color: var(--ds-text-body);
}

.agent-help__link {
  color: var(--ds-brand-primary-dark);
  font-weight: 500;
  font-size: 15px;
  text-decoration: underline;
  text-underline-offset: 2px;
}

/* The support route is a button, not a link — it opens the in-chat dialog
   rather than navigating — but it has to read identically to "Email us" so
   neither looks like the lesser option. */
.agent-help__link--button {
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  cursor: pointer;
}

.agent-help__timing {
  margin: 0 0 20px;
  font-size: 13px;
  line-height: 1.4;
  color: var(--ds-text-tertiary, rgba(0, 0, 0, 0.55));
}

.agent-help__faq-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  background: none;
  border: none;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding: 14px 0 0;
  font-family: inherit;
  font-size: 15px;
  color: var(--ds-brand-primary-dark);
  text-align: left;
  cursor: pointer;
}

.agent-help__faqs {
  margin-top: 12px;
}

.agent-help__faqs-body {
  font-size: 14px;
  line-height: 1.5;
  color: var(--ds-text-body);
}
</style>
